import { useState, FormEvent, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, TextButton } from '@safetyhub/ui';
import { authApi, verificationApi, extractApiError } from '../../lib/auth';

export default function SignupEmail() {
  const [searchParams] = useSearchParams();

  // 초대 관련 쿼리 파라미터
  const inviteToken = searchParams.get('inviteToken');
  const prefillEmail = searchParams.get('email');

  const [email, setEmail] = useState(prefillEmail || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);

  // 초대 이메일로 미리 채우기
  useEffect(() => {
    if (prefillEmail && !email) {
      setEmail(prefillEmail);
    }
  }, [prefillEmail]);

  // 이메일 유효성 검사
  const isValidEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  // 이메일 중복 확인
  const checkEmailAvailability = async () => {
    if (!isValidEmail(email)) return;

    try {
      const result = await verificationApi.checkEmail(email);
      setEmailAvailable(result.available);
      if (!result.available) {
        setError('이미 가입된 이메일입니다.');
      } else {
        setError('');
      }
    } catch {
      // 무시
    }
  };

  // 가입 메일 발송
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('이메일을 입력해주세요.');
      return;
    }

    if (!isValidEmail(email)) {
      setError('올바른 이메일 형식이 아닙니다.');
      return;
    }

    // 초대 이메일이 아닌 경우에만 중복 체크 (초대 이메일은 이미 검증됨)
    if (!prefillEmail && emailAvailable === false) {
      setError('이미 가입된 이메일입니다.');
      return;
    }

    setIsLoading(true);

    try {
      await authApi.initiateEmailSignup(email.trim());
      // 초대 토큰이 있으면 localStorage에 저장 (email-complete 페이지에서 사용)
      if (inviteToken) {
        localStorage.setItem('pendingInviteToken', inviteToken);
      }
      setSuccess(true);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 성공 화면
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-block">
              <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
            </Link>
            <div className="mt-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="mt-4 text-2xl font-bold text-ink-900">이메일을 확인해주세요</h2>
              <p className="mt-4 text-ink-600">
                <span className="font-medium text-brand-600">{email}</span>
                으로 가입 링크를 발송했습니다.
              </p>
              <p className="mt-2 text-sm text-ink-500">
                메일함을 확인하고 링크를 클릭하여 가입을 완료해주세요.
                <br />
                링크는 24시간 동안 유효합니다.
              </p>
            </div>

            <div className="mt-8 space-y-4">
              <p className="text-sm text-ink-500">이메일이 도착하지 않았나요?</p>
              <TextButton onClick={() => setSuccess(false)} className="font-medium">
                다시 시도하기
              </TextButton>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-ink-500">
            SafetyHub &copy; 2025. All rights reserved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
          </Link>
          <h2 className="mt-4 text-2xl font-bold text-ink-900">이메일로 가입</h2>
          <p className="mt-2 text-sm text-ink-600">
            <Link to="/signup" className="font-medium text-brand-600 hover:text-brand-500">
              &larr; 다른 방법으로 가입
            </Link>
          </p>
        </div>

        {/* 초대 알림 */}
        {inviteToken && (
          <div className="bg-green-50 border border-green-200 rounded-card p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">조직 초대가 있습니다</p>
                <p className="mt-1 text-sm text-green-700">
                  회원가입 완료 시 자동으로 초대가 수락됩니다.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div className="rounded-card bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* 안내 */}
        <div className="bg-blue-50 rounded-card p-4">
          <p className="text-sm text-blue-800">
            이메일 주소를 입력하면 가입 링크가 발송됩니다.
            <br />
            메일의 링크를 클릭하여 가입을 진행해주세요.
          </p>
        </div>

        {/* 이메일 입력 폼 */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-700">
              이메일 주소 <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={prefillEmail ? undefined : (e) => {
                setEmail(e.target.value);
                setEmailAvailable(null);
                setError('');
              }}
              onBlur={prefillEmail ? undefined : checkEmailAvailability}
              disabled={isLoading}
              readOnly={!!prefillEmail}
              className={`mt-1 w-full px-3 py-2 border rounded-control focus:ring-brand-500 focus:border-brand-500 ${
                prefillEmail
                  ? 'bg-ink-100 text-ink-700 cursor-not-allowed'
                  : emailAvailable === true
                  ? 'border-green-300'
                  : emailAvailable === false
                  ? 'border-red-300'
                  : 'border-ink-300'
              }`}
              placeholder="example@company.com"
            />
            {prefillEmail && (
              <p className="mt-1 text-sm text-ink-500">
                초대받은 이메일 주소로 고정되어 있습니다.
              </p>
            )}
            {!prefillEmail && emailAvailable === true && (
              <p className="mt-1 text-sm text-green-600">사용 가능한 이메일입니다.</p>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            disabled={isLoading || !isValidEmail(email) || (!prefillEmail && emailAvailable === false)}
            className="w-full"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                발송 중...
              </span>
            ) : (
              '가입 메일 받기'
            )}
          </Button>
        </form>

        {/* 푸터 */}
        <p className="mt-8 text-center text-xs text-ink-500">
          SafetyHub &copy; 2025. All rights reserved.
        </p>
      </div>
    </div>
  );
}
